import type { SqlDriver } from '../data/db/driver';
import type { Store } from '../data/store/store';
import type { BudgetMember } from '../domain/model';
import { pushOutbox, reconcileAccess, syncOnce, type PushOptions } from './engine';
import type { AcceptResult, Invite, SyncTransport } from './types';

/**
 * Household sharing on the device (Phase 18). The server owns who may see
 * and write a budget (supabase/migrations/…_sharing.sql); these flows keep
 * the local database in step with it.
 */

const CODE_ALPHABET = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/;

/** "abcd efgh", "ABCD-EFGH" or a whole invite link → "ABCDEFGH"; null when it is not a code. */
export function parseInviteCode(input: string): string | null {
  const fromLink = input.match(/\/join\/([A-Za-z0-9-]+)/)?.[1];
  const code = (fromLink ?? input).toUpperCase().replace(/[^A-Z0-9]/g, '');
  return CODE_ALPHABET.test(code) ? code : null;
}

/** Shown and read aloud in two halves: ABCD-EFGH. */
export const formatInviteCode = (code: string) => `${code.slice(0, 4)}-${code.slice(4)}`;

/** The link in the invite (web app address + /join/CODE); null when this build has no public address. */
export function inviteLink(baseUrl: string | null, code: string): string | null {
  return baseUrl ? `${baseUrl.replace(/\/+$/, '')}/join/${formatInviteCode(code)}` : null;
}

/** Make sure the budget is on the server (it may still sit in the outbox), then ask for a code. */
export async function createInvite(db: SqlDriver, transport: SyncTransport, spaceId: string, options: PushOptions = {}): Promise<Invite> {
  await pushOutbox(db, transport, options);
  return transport.createInvite(spaceId);
}

export type JoinResult = AcceptResult & { memberId?: string };

/**
 * Accept a code: the server lets this account in, the budget comes down
 * whole, this person's member row is added (or renamed) and goes up.
 */
export async function joinBudget(
  db: SqlDriver,
  store: Store,
  transport: SyncTransport,
  input: { code: string; userId: string; displayName: string },
  options: PushOptions = {},
): Promise<JoinResult> {
  const res = await transport.acceptInvite(input.code);
  if (!res.ok) return res;
  // Brings the budget down whole (it is new to this device).
  await reconcileAccess(db, transport);
  const displayName = input.displayName.trim();
  let me: BudgetMember | undefined = await store.members.forUser(res.spaceId, input.userId);
  if (!me) {
    // Back after leaving (or being removed): the same person again, so their earlier entries stay theirs.
    const before = (await store.members.list({ spaceId: res.spaceId, userId: input.userId }, { includeDeleted: true }))[0];
    me = before
      ? await store.members.restore(before.id)
      : await store.members.create({ spaceId: res.spaceId, userId: input.userId, displayName, role: 'member' });
  }
  if (displayName && me.displayName !== displayName) me = await store.members.update(me.id, { displayName });
  // A member does not need Premium (the owner's covers the budget); their own budgets stay held if they have none.
  await syncOnce(db, transport, options);
  return { ...res, memberId: me.id };
}

/** Owner: take someone out. Their devices stop getting the budget; the entries they added stay. */
export async function removeMember(store: Store, transport: SyncTransport, member: BudgetMember): Promise<void> {
  if (member.role === 'owner') throw new Error('the owner cannot be removed');
  if (member.userId) await transport.removeMember(member.spaceId, member.userId);
  await store.members.remove(member.id);
}

/**
 * Member: leave a shared budget. The others see you leave (your member row
 * goes up as removed first); it disappears from this device and, next
 * sync, from your others.
 */
export async function leaveBudget(
  db: SqlDriver,
  store: Store,
  transport: SyncTransport,
  spaceId: string,
  userId: string,
  options: PushOptions = {},
): Promise<void> {
  const me = await store.members.forUser(spaceId, userId);
  if (me?.role === 'owner') throw new Error('the owner cannot leave');
  if (me) await store.members.remove(me.id);
  try {
    await pushOutbox(db, transport, options);
    await transport.leaveSpace(spaceId);
  } catch (e) {
    // Still in (offline?): stay a member here too, and try again later.
    if (me) await store.members.restore(me.id);
    throw e;
  }
  await db.batch([
    { sql: 'UPDATE budget_spaces SET deleted_at = ? WHERE id = ? AND deleted_at IS NULL', params: [Date.now(), spaceId] },
    { sql: 'DELETE FROM sync_outbox WHERE space_id = ? OR (table_name = ? AND row_id = ?)', params: [spaceId, 'budget_spaces', spaceId] },
  ]);
}
